import React, {useEffect, useRef, useState} from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    CardContent,
    IconButton,
    Menu,
    MenuItem,
    Slider,
    Stack,
    Typography,
} from "@mui/material";
import {
    ExpandMore,
    Pause,
    PlayArrow,
    Replay,
    Forward,
    Speed,
    VolumeOff,
    VolumeUp,
} from "@mui/icons-material";
import {formatAudioTime, SPEED_OPTIONS, useAudioPlayback, isValidAudioUrl} from "./useAudioPlayback";

export {formatAudioTime, isValidAudioUrl};

export const AccessibleAudioPlayer = ({
    src,
    title,
    subtitle,
    transcript,
    autoPlay = false,
}) => {
    const playerCardRef = useRef(null);
    const [speedAnchorEl, setSpeedAnchorEl] = useState(null);

    const {
        audioRef,
        isUrlSafe,
        isPlaying,
        currentTime,
        duration,
        playbackRate,
        isMuted,
        errorMessage,
        liveAnnouncement,
        togglePlay,
        handleSkip,
        handleSpeedChange,
        handleToggleMute,
        handleSeek,
        audioHandlers,
    } = useAudioPlayback({src, title});

    // Stable ref for keyboard events to prevent listener thrashing
    const controlsRef = useRef({togglePlay, handleSkip, handleToggleMute});
    useEffect(() => {
        controlsRef.current = {togglePlay, handleSkip, handleToggleMute};
    }, [togglePlay, handleSkip, handleToggleMute]);

    // Scoped keyboard navigation (does not hijack other inputs or rich-text editors)
    useEffect(() => {
        const handleKeyDown = (e) => {
            const target = e.target;
            // Ignore if typing inside input, textarea, contenteditable editor, or navigating slider thumb
            if (
                target &&
                (target.tagName === "INPUT" ||
                    target.tagName === "TEXTAREA" ||
                    target.isContentEditable ||
                    (target.getAttribute && target.getAttribute("role") === "slider"))
            ) {
                return;
            }

            const isPlayerFocused = playerCardRef.current && playerCardRef.current.contains(target);
            const isBodyFocused = target === document.body;

            if (e.key === " " || e.key === "k") {
                if (isPlayerFocused || isBodyFocused) {
                    e.preventDefault();
                    controlsRef.current.togglePlay();
                }
            } else if (isPlayerFocused && e.key === "ArrowLeft") {
                e.preventDefault();
                controlsRef.current.handleSkip(-15);
            } else if (isPlayerFocused && e.key === "ArrowRight") {
                e.preventDefault();
                controlsRef.current.handleSkip(15);
            } else if (isPlayerFocused && e.key === "m") {
                controlsRef.current.handleToggleMute();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    return (
        <Card
            ref={playerCardRef}
            tabIndex={0}
            aria-label={`Lydspiller: ${title}`}
            sx={{
                borderRadius: 3,
                p: {xs: 1.5, sm: 3},
                mb: 3,
                position: "relative",
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 4px 20px rgba(40, 82, 56, 0.08)",
                outline: "none",
                "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: "2px",
                },
            }}
        >
            {isUrlSafe && (
                <audio
                    ref={audioRef}
                    src={src}
                    autoPlay={autoPlay}
                    preload="metadata"
                    onLoadedMetadata={audioHandlers.onLoadedMetadata}
                    onTimeUpdate={audioHandlers.onTimeUpdate}
                    onEnded={audioHandlers.onEnded}
                    onError={audioHandlers.onError}
                    onPlay={audioHandlers.onPlay}
                    onPause={audioHandlers.onPause}
                />
            )}

            {/* Screen reader live announcement region */}
            <Box
                aria-live="polite"
                aria-atomic="true"
                sx={{
                    position: "absolute",
                    width: "1px",
                    height: "1px",
                    p: 0,
                    margin: "-1px",
                    overflow: "hidden",
                    clip: "rect(0, 0, 0, 0)",
                    whiteSpace: "nowrap",
                    border: 0,
                    left: 0,
                    top: 0,
                }}
            >
                {liveAnnouncement}
            </Box>

            <CardContent sx={{p: 0, "&:last-child": {pb: 0}}}>
                {/* Header Information */}
                <Box sx={{mb: 2, textAlign: {xs: "center", sm: "left"}, minWidth: 0, maxWidth: "100%"}}>
                    <Typography
                        variant="h6"
                        component="h2"
                        sx={{
                            fontWeight: 700,
                            color: "primary.main",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                        }}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                mt: 0.5,
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>

                {errorMessage && (
                    <Typography
                        role="alert"
                        color="error"
                        variant="body2"
                        sx={{mb: 2, textAlign: "center"}}
                    >
                        {errorMessage}
                    </Typography>
                )}

                {/* Progress / Seek bar */}
                <Box sx={{px: 1, mb: 1, minWidth: 0, maxWidth: "100%"}}>
                    <Slider
                        value={currentTime}
                        min={0}
                        max={duration || 100}
                        step={1}
                        onChange={handleSeek}
                        aria-label="Fremdrift for meditasjonslyd"
                        aria-valuetext={`${formatAudioTime(currentTime)} av ${formatAudioTime(duration)}`}
                        sx={{
                            color: "primary.main",
                            height: 6,
                            "& .MuiSlider-thumb": {
                                width: 16,
                                height: 16,
                                transition: "0.2s cubic-bezier(.47,1.64,.41,.8)",
                                "&:before": {
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.16)",
                                },
                                "&:hover, &.Mui-focusVisible": {
                                    boxShadow: "0px 0px 0px 8px rgba(40, 82, 56, 0.16)",
                                },
                            },
                        }}
                    />
                    <Stack direction="row" justifyContent="space-between" sx={{mt: -0.5}}>
                        <Typography variant="caption" sx={{color: "text.secondary", fontWeight: 500}}>
                            {formatAudioTime(currentTime)}
                        </Typography>
                        <Typography variant="caption" sx={{color: "text.secondary", fontWeight: 500}}>
                            {formatAudioTime(duration)}
                        </Typography>
                    </Stack>
                </Box>

                {/* Audio Controls Bar */}
                <Box
                    sx={{
                        py: 1,
                        width: "100%",
                        minWidth: 0,
                        maxWidth: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: {xs: "wrap", sm: "nowrap"},
                        gap: 1,
                    }}
                >
                    {/* Speed Selector */}
                    <Box sx={{order: {xs: 1, sm: 1}}}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Speed sx={{fontSize: 16}} />}
                            onClick={(e) => setSpeedAnchorEl(e.currentTarget)}
                            aria-label={`Hastighet ${playbackRate}x`}
                            sx={{
                                minHeight: 44,
                                minWidth: 44,
                                borderColor: "divider",
                                color: "text.primary",
                                borderRadius: 4,
                                px: {xs: 1, sm: 1.5},
                                py: 0.5,
                                fontSize: "0.8rem",
                                "&:focus-visible": {
                                    outline: "2px solid #285238",
                                    outlineOffset: "2px",
                                },
                            }}
                        >
                            {playbackRate.toFixed(1)}x
                        </Button>
                        <Menu
                            anchorEl={speedAnchorEl}
                            open={Boolean(speedAnchorEl)}
                            onClose={() => setSpeedAnchorEl(null)}
                        >
                            {SPEED_OPTIONS.map((rate) => (
                                <MenuItem
                                    key={rate}
                                    selected={playbackRate === rate}
                                    onClick={() => {
                                        handleSpeedChange(rate);
                                        setSpeedAnchorEl(null);
                                    }}
                                >
                                    {rate.toFixed(2).replace(/\.00$/, ".0")}x
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>

                    {/* Central Playback Controls */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: {xs: 1, sm: 1.5},
                            order: {xs: 3, sm: 2},
                            width: {xs: "100%", sm: "auto"},
                            justifyContent: "center",
                            mt: {xs: 0.5, sm: 0},
                        }}
                    >
                        <IconButton
                            onClick={() => handleSkip(-15)}
                            aria-label="Spol 15 sekunder tilbake"
                            size="small"
                            sx={{
                                minWidth: 44,
                                minHeight: 44,
                                width: 44,
                                height: 44,
                                color: "primary.main",
                                "&:hover": {backgroundColor: "rgba(40, 82, 56, 0.08)"},
                                "&:focus-visible": {
                                    outline: "2px solid #285238",
                                    outlineOffset: "2px",
                                },
                            }}
                        >
                            <Replay />
                        </IconButton>

                        <IconButton
                            onClick={togglePlay}
                            aria-label={isPlaying ? "Sett på pause" : "Spill av"}
                            sx={{
                                backgroundColor: "primary.main",
                                color: "#FFFFFF",
                                width: {xs: 50, sm: 60},
                                height: {xs: 50, sm: 60},
                                minWidth: {xs: 50, sm: 60},
                                minHeight: {xs: 50, sm: 60},
                                boxShadow: "0 4px 14px rgba(40, 82, 56, 0.25)",
                                "&:hover": {
                                    backgroundColor: "primary.dark",
                                },
                                "&:focus-visible": {
                                    outline: "3px solid #285238",
                                    outlineOffset: "3px",
                                },
                            }}
                        >
                            {isPlaying ? (
                                <Pause sx={{fontSize: {xs: 28, sm: 34}}} />
                            ) : (
                                <PlayArrow sx={{fontSize: {xs: 28, sm: 34}, ml: 0.3}} />
                            )}
                        </IconButton>

                        <IconButton
                            onClick={() => handleSkip(15)}
                            aria-label="Spol 15 sekunder frem"
                            size="small"
                            sx={{
                                minWidth: 44,
                                minHeight: 44,
                                width: 44,
                                height: 44,
                                color: "primary.main",
                                "&:hover": {backgroundColor: "rgba(40, 82, 56, 0.08)"},
                                "&:focus-visible": {
                                    outline: "2px solid #285238",
                                    outlineOffset: "2px",
                                },
                            }}
                        >
                            <Forward />
                        </IconButton>
                    </Box>

                    {/* Mute Toggle */}
                    <Box sx={{order: {xs: 2, sm: 3}}}>
                        <IconButton
                            onClick={handleToggleMute}
                            aria-label={isMuted ? "Slå på lyd" : "Demp lyd"}
                            size="small"
                            sx={{
                                minWidth: 44,
                                minHeight: 44,
                                width: 44,
                                height: 44,
                                color: "text.secondary",
                                "&:focus-visible": {
                                    outline: "2px solid #285238",
                                    outlineOffset: "2px",
                                },
                            }}
                        >
                            {isMuted ? <VolumeOff /> : <VolumeUp />}
                        </IconButton>
                    </Box>
                </Box>

                {/* Collapsible Transcript (WCAG 1.2.1 Level A) */}
                {transcript && (
                    <Accordion
                        disableGutters
                        elevation={0}
                        sx={{
                            mt: 2,
                            borderTop: "1px solid",
                            borderColor: "divider",
                            "&:before": {display: "none"},
                            backgroundColor: "transparent",
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMore sx={{color: "primary.main"}} />}
                            aria-controls="transcript-content"
                            id="transcript-header"
                            sx={{
                                px: 0.5,
                                minHeight: 48,
                                "&:focus-visible": {
                                    outline: "2px solid #285238",
                                    outlineOffset: "2px",
                                },
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: "primary.main",
                                }}
                            >
                                Vis tekstutskrift (Transkripsjon)
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails id="transcript-content" sx={{px: 0.5, pt: 1, pb: 2}}>
                            <Typography
                                variant="body2"
                                component="div"
                                sx={{
                                    whiteSpace: "pre-line",
                                    lineHeight: 1.8,
                                    color: "text.primary",
                                    fontSize: "0.95rem",
                                }}
                            >
                                {transcript}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                )}
            </CardContent>
        </Card>
    );
};
